// Lib
import React, { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { cloneDeep, sum } from 'lodash';
import { Events } from 'handsontable/pluginHooks';
import Handsontable from 'handsontable/base';

// Utils
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { convertColWidthsGUtoPx, getColWidthsResizeWidth, getNewColumnWidthsPx } from '../utils/tableSizeUtils';
import useThrottledCallback from '../../../utils/react/useThrottledCallback';
import { getTableDOMElementById } from '../utils/tableDOMUtils';
import { parseCssPx } from '../../../utils/cssUtil';
import { stopDefaultAndPropagation } from '../../../utils/domUtil';
import { getAllSelectedCols, isColInSelections, isRowInSelection } from '../utils/tableCellSelectionUtils';
import { getColumnCount } from '../utils/tableDataUtils';

// Types
import TableOperations from './TableOperations';
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { CellSelections, TableContent } from '../../../../common/table/TableTypes';

// Constants
import { TABLE_CELL_MIN_WIDTH, TABLE_DEFAULT_COL_WIDTH } from '../../../../common/table/tableConstants';

interface Props {
    element: MNElement;
    tableContentObject: TableContent;
    gridSize: number;
    tableContentColWidthsGU: number[];
    tableContentData: string[][];
    dispatchUpdateTableElement: (args: object) => void;
    tableOperationsRef: React.RefObject<TableOperations>;
    isPresentational: boolean;
    hotTableContainerRef: React.RefObject<HTMLDivElement>;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
    getContextZoomScale: () => number;
    inList: string | null;
    isResizing: boolean;
}

interface RefType {
    beforeColumnResize: Events['beforeColumnResize'];
}

const TableColumnResizeHandlers = forwardRef<RefType, Props>(function TableColumnResizeHandlersComponent(props, ref) {
    const {
        element,
        gridSize,
        tableContentColWidthsGU,
        tableContentData,
        dispatchUpdateTableElement,
        tableOperationsRef,
        hotTableContainerRef,
        hotTableInstanceRef,
        getContextZoomScale,
        inList,
        isResizing,
    } = props;

    const colResizeInstance = hotTableInstanceRef?.current?.getPlugin<'manualColumnResize'>('manualColumnResize');
    const elementId = getElementId(element);
    const tableElement = getTableDOMElementById(elementId);
    const nCols = getColumnCount(tableContentData);

    // **********************************
    // COLUMN RESIZE AND POSITIONING
    // **********************************

    const liveResize = useThrottledCallback(
        (event: MouseEvent) => {
            if (!colResizeInstance) return;

            const { dragStartPos = 0, tableStartWidth, colValues } = colResizeInstance.milanoteValues;
            if (!colValues?.length || !tableStartWidth) return;

            const zoomScale = getContextZoomScale();
            const mousePos = event.clientX / zoomScale;
            const dif = mousePos - dragStartPos;

            const initialColWidths = colValues.map((col) => col.initialWidth);

            const minTableCellWidthPx = TABLE_CELL_MIN_WIDTH * gridSize - 1;
            const newColWidths = getNewColumnWidthsPx(
                colResizeInstance.selectedCols,
                initialColWidths,
                minTableCellWidthPx,
                dif,
            );

            // Visually update the col widths
            colResizeInstance.selectedCols.forEach((col, index) => {
                const newWidth = newColWidths[index];
                const { headerColElement, bodyColElement } = colValues[index];

                // Add the new width to the width map in colResize plugin
                colResizeInstance.columnWidthsMap.setValueAtIndex(col, newWidth);

                // Better for performance to set the width directly on the col elements rather than using the
                // colResizeInstance.setManualSize method
                if (headerColElement) headerColElement.style.width = `${newWidth}px`;
                if (bodyColElement) bodyColElement.style.width = `${newWidth}px`;
            });

            // Temporarily set the table element width while resizing
            if (tableElement) {
                const actualWidthChange = sum(newColWidths) - sum(initialColWidths);
                tableElement.style.width = `${tableStartWidth + actualWidthChange}px`;
            }
        },
        10,
        [tableElement, colResizeInstance, gridSize],
    );

    const finishResizing = useCallback(() => {
        document.removeEventListener('mouseup', finishResizing);
        document.removeEventListener('pointerup', finishResizing);
        document.removeEventListener('mousemove', liveResize);
        document.removeEventListener('pointermove', liveResize);

        hotTableContainerRef.current?.classList.remove('col-resizing');

        hotTableInstanceRef.current?.resumeRender();

        colResizeInstance?.onMouseUp();
    }, [hotTableContainerRef, colResizeInstance, hotTableInstanceRef]);

    const initialiseColResize = useCallback(
        (event) => {
            stopDefaultAndPropagation(event);

            if (!colResizeInstance) return;

            // get element from event and use that to check the index of the col
            const column = colResizeInstance.hot.getCoords(event.target).col;

            // We want to suspend render while resizing to improve performance
            // and stop flickering of borders, background colour etc. while rendering.
            // This means that selection borders won't be updated though, so for now we
            // are only suspending render when there aren't any selections
            const selections = hotTableInstanceRef?.current?.getSelected() as CellSelections;
            if (!selections) hotTableInstanceRef.current?.suspendRender();

            hotTableContainerRef.current?.classList.add('col-resizing');

            //****************** Add resizing event listeners ***********************

            document.addEventListener('mouseup', finishResizing);
            document.addEventListener('pointerup', finishResizing);
            document.addEventListener('mousemove', liveResize);
            document.addEventListener('pointermove', liveResize);

            //****************** Update colResizeInstance with accurate values ***********************

            // Update the selectedCols array
            const newResizingCols: number[] = [];

            // Get all the selections that include the header row
            const headerSelections = selections?.reduce(
                (acc, selection) => (isRowInSelection(-1, selection) ? [...acc, selection] : acc),
                [] as CellSelections,
            );

            // if the header ref is selected, and the current col is selected, we need to resize all selected cols
            if (headerSelections && isColInSelections(column, headerSelections)) {
                getAllSelectedCols(headerSelections).forEach((col) => {
                    newResizingCols.push(col);
                });
            } else {
                // There are no header selections, so we just resize the current col
                newResizingCols.push(column);
            }
            colResizeInstance.selectedCols = newResizingCols;

            const colValues = colResizeInstance.selectedCols.map((col) => {
                return {
                    initialWidth: hotTableInstanceRef.current?.getColWidth(col) || 0,
                    headerColElement: colResizeInstance.headerColElements[col] || null,
                    bodyColElement: colResizeInstance.bodyColElements[col] || null,
                };
            });

            // Set the timer for when we double-click
            if (colResizeInstance.autoresizeTimeout === null) {
                // @ts-ignore - incorrect in handsontable types
                colResizeInstance.autoresizeTimeout = setTimeout(() => colResizeInstance.afterMouseDownTimeout(), 500);
            }

            const zoomScale = getContextZoomScale();
            const dragStartPos = event.clientX / zoomScale;

            colResizeInstance.startWidth = 0;
            colResizeInstance.pressed = true;
            colResizeInstance.currentCol = column;
            colResizeInstance.currentTH = event.target.parentElement;
            colResizeInstance.dblclick += 1;
            colResizeInstance.milanoteValues = {
                colValues,
                dragStartPos,
                tableStartWidth: parseCssPx(tableElement?.style.width || '0px'),
                isCurrentlyResizing: true,
            };
        },
        [colResizeInstance, tableElement, hotTableInstanceRef],
    );

    const updateColResizeHandles = useCallback(() => {
        if (!colResizeInstance) return;

        // Get a list of all the relevant column header elements
        const colRefHeaderElement = colResizeInstance.hot.rootElement.querySelector('.ht_clone_top');
        const thElements = Array.from(colRefHeaderElement?.querySelectorAll('th') || []);
        // Remove the first element which is the row header (not visible)
        thElements.shift();

        // Add the resize handle to each column header
        const { rootDocument } = colResizeInstance.hot;
        thElements.forEach((thElement, index) => {
            // only run if there isn't an existing handle
            if (thElement.querySelector('.manualColumnResizerHandle')) return;

            const newHandle = rootDocument.createElement('DIV');
            newHandle.className = 'manualColumnResizerHandle';

            newHandle.addEventListener('mousedown', (event: Event) => initialiseColResize(event));
            newHandle.addEventListener('pointerdown', (event: Event) => initialiseColResize(event));

            // On iPad, the pointermove event will be overridden by a touchmove event, which will scroll the canvas instead
            // of resizing the column. The following code will let TouchBackend.ts prevent this behaviour.
            newHandle.dataset.ignoreTouchMove = 'true';

            thElement.appendChild(newHandle);
        });

        colResizeInstance.headerColElements = colResizeInstance.hot.rootElement.querySelectorAll<HTMLTableColElement>(
            '.ht_clone_top colgroup col:not(.rowHeader)',
        );

        colResizeInstance.bodyColElements = colResizeInstance.hot.rootElement.querySelectorAll<HTMLTableColElement>(
            '.ht_master colgroup col:not(.rowHeader)',
        );
    }, [colResizeInstance]);

    /**
     * This effect is used to adjust default event listeners in the col resize plugin,
     * and to initialise the resize handles on the column headers
     */
    useEffect(() => {
        if (!colResizeInstance) return;

        const colResizeEventManager = colResizeInstance.eventManager;

        // We don't want the default handle positioning, as we have our own handles
        // and sometimes this function sets the resizing column incorrectly
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        colResizeInstance.setupHandlePosition = () => {};

        // Remove the default mousemove event listener
        // @ts-ignore - valid property
        const mouseMoveEvent = colResizeEventManager.context.eventListeners?.find(({ event }) => event === 'mousemove');
        if (mouseMoveEvent) {
            const { element, event: eventName, callback } = mouseMoveEvent;
            colResizeEventManager.removeEventListener(element, eventName, callback);
        }

        // Remove the default mousedown event listener
        // @ts-ignore - valid property
        const mouseDownEvent = colResizeEventManager.context.eventListeners?.find(({ event }) => event === 'mousedown');
        if (mouseDownEvent) {
            const { element, event: eventName, callback } = mouseDownEvent;
            colResizeEventManager.removeEventListener(element, eventName, callback);
        }

        updateColResizeHandles();
    }, [colResizeInstance]);

    /**
     * This effect is used to update the resize handles as columns are added
     */
    useEffect(() => {
        // we only want to run when the resizing has finished
        if (isResizing) return;

        // timeout just to improve performance as it won't be used immediately
        setTimeout(() => updateColResizeHandles());
    }, [isResizing, nCols]);

    // **********************************
    // HANDSONTABLE COLUMN RESIZE HOOKS
    // **********************************

    const beforeColumnResize = useCallback(
        (newSize, _column, isDoubleClick) => {
            // If it's a double click, we just need to account for the zoomScale

            if (!isDoubleClick) return;

            const zoomScale = getContextZoomScale();
            const newSizePx = newSize / zoomScale;

            const minTableCellWidthPx = TABLE_CELL_MIN_WIDTH * gridSize - 1;
            return Math.max(newSizePx, minTableCellWidthPx);
        },
        [colResizeInstance],
    );

    const afterColumnResize = useCallback(
        (newSize, column) => {
            if (!hotTableInstanceRef.current) return;

            // the newSize value coming from handsontable will always be the same for each column
            // (e.g. 2 columns of different initial widths might both have 100px as the new size),
            // so for multiple columns being resized we need to get the actual new size from the columnWidthsMap
            newSize = colResizeInstance?.columnWidthsMap.getValueAtIndex(column) || newSize;

            // When we convert the col widths from Gu to Px, we have to account for the border width
            // and remove 1px from each col except the first one, therefore when we are starting in Px and converting
            // to Gu we must add 1px to each col except the first one
            const borderOffset = column === 0 ? 0 : 1;
            const newColWidthGU = (newSize + borderOffset) / gridSize;

            const newColWidthsGU = cloneDeep(tableContentColWidthsGU);
            newColWidthsGU[column] = newColWidthGU;

            // If this is the final column to be resized, we can reset the milanoteValues
            const selectedCols = colResizeInstance?.selectedCols;
            if (selectedCols && selectedCols[selectedCols.length - 1] === column) {
                colResizeInstance.milanoteValues = {};
            }

            dispatchUpdateTableElement({
                id: elementId,
                changes: {
                    tableContent: {
                        data: tableContentData,
                        colWidthsGU: newColWidthsGU,
                    },
                    width: newColWidthsGU.reduce((sum, val) => sum + val, 0),
                },
            });
        },
        [gridSize, elementId, tableContentColWidthsGU, tableContentData, colResizeInstance],
    );

    // **********************************
    // MAKE SURE COL WIDTHS PX ARE UPDATED
    // **********************************

    // Update col widths px in hot table based on container size
    const updateColWidthsPx = useThrottledCallback<void, void>(
        () => {
            const resizeToWidth = getColWidthsResizeWidth(element, inList, hotTableContainerRef, getContextZoomScale);
            const tableContentColWidthsPx = convertColWidthsGUtoPx(tableContentColWidthsGU, gridSize, resizeToWidth);

            if (!tableContentColWidthsPx) return;

            let isUpdated = false;
            tableContentColWidthsPx.forEach((colWidthPx, index) => {
                const currColWidth = hotTableInstanceRef.current?.getColWidth(index);
                if (currColWidth === colWidthPx) return;

                tableOperationsRef.current?.updateColWidth(index, colWidthPx);
                isUpdated = true;
            });

            if (isUpdated) requestAnimationFrame(() => hotTableInstanceRef.current?.render());
        },
        10,
        [tableContentColWidthsGU, gridSize, inList],
    );

    /**
     * Observe changes in hot table size, and update col widths px
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || !inList) return;
        const resizeObserver = new ResizeObserver(() => updateColWidthsPx());
        resizeObserver.observe(hotTableInstanceRef.current?.rootElement);
        return () => resizeObserver.disconnect();
    }, []);

    /**
     * Recalculate col widths px when table content col widths GU change
     * e.g. when a column is added/removed, or when a column is moved
     */
    useEffect(() => {
        // We don't want to update hot table if we are still resizing
        // this is because if we are resizing multiple columns, this function can be called before all the colWidthGu
        // values have been accurately updated in the afterColumnResize function
        if (colResizeInstance && colResizeInstance?.milanoteValues?.isCurrentlyResizing) return;

        updateColWidthsPx();
    }, [tableContentColWidthsGU, gridSize, colResizeInstance]);

    /**
     * Keep the min col width value accurate in the hot table.
     */
    const getDefaultColWidth = useCallback(() => {
        return TABLE_DEFAULT_COL_WIDTH * gridSize;
    }, [gridSize]);

    useImperativeHandle(
        ref,
        () => ({
            beforeColumnResize,
            afterColumnResize,
            getDefaultColWidth,
        }),
        [beforeColumnResize, afterColumnResize, getDefaultColWidth],
    );

    return null;
});

export default TableColumnResizeHandlers;
